/**
 * CalendarFilterPill: the calendar's filters behind one floating pill, drawn as the Practice calendar draws it
 * (Antonin, 16 Oct: "clean the filter button like it is in Practice"): a round funnel button at the top right of
 * the grid, just under its header, that opens sideways into one pill when the pointer (or the keyboard) reaches it
 * and folds back 300 ms after it leaves. No gutter and no popover under it: it floats over the grid and never
 * pushes it.
 *
 * Moved out of the Signal DS `PACalendarSubToolbar` row, same controls and same behaviour, same store:
 *   - Waiting, No show, Cancelled: each toggles one status code (with the counts from the response);
 *   - Statuses: a list of the status codes with Select all / Clear all (the DS rule: the same
 *     `selectedStatusCodes` set the three quick filters edit);
 *   - Invoice statuses: a list over Draft / Issued / Balanced;
 *   - the practice's locations (Office, Home, Virtual, ...), one toggle each.
 * A filter that is on shows on the folded pill itself: a blue count badge, so a narrowed calendar never looks
 * complete. The pill stays open while one of its lists is open. Looks: the bridge's "calendar filter pill" block.
 */
import { useRef, useState } from 'react';
import { Ban, Check, ChevronDown, CircleX, Clock, Funnel, Receipt, type LucideIcon } from 'lucide-react';
import { locationGlyph } from '@/scheduling/lib/appointment-glyphs';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  type PACalendarSubToolbarProps,
  type PACalendarLocation,
} from '@scripsteam/scrips-signal-ds';
import { APPOINTMENT_STATUS_CODES, INVOICE_STATUS_OPTIONS } from '@/types/scheduling-api';

type FilterSeams = Pick<
  PACalendarSubToolbarProps,
  | 'selectedStatusCodes'
  | 'onToggleStatusCode'
  | 'onSetStatusCodes'
  | 'onClearStatusCodes'
  | 'selectedInvoiceStatuses'
  | 'onToggleInvoiceStatus'
  | 'onSetInvoiceStatuses'
  | 'onClearInvoiceStatuses'
  | 'selectedLocationIds'
  | 'onToggleLocation'
>;

export interface CalendarFilterPillProps extends FilterSeams {
  waitingRoomCount?: number;
  noShowCount?: number;
  cancelledCount?: number;
  /** Options of the Statuses list, from /api/Appointment/Status; the DS code list while it loads. */
  statusOptions?: { code: string; display: string }[];
  /** The practice's locations: one toggle each. */
  organizationLocations?: PACalendarLocation[];
}

/** How long the pill stays open after the pointer leaves it (Practice: 300 ms). */
const CLOSE_DELAY_MS = 300;

/** Location name → glyph: the same marks as the booking tiles, the provider card and the day rows. */
const iconForLocation = (name: string): LucideIcon => locationGlyph(name, 'block');

function QuickFilter({
  icon: Glyph,
  label,
  count,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick} className="cal-filter-chip">
      <Glyph aria-hidden />
      <span>{label}</span>
      {typeof count === 'number' && count > 0 ? <span className="cal-filter-count">{count}</span> : null}
    </button>
  );
}

function ListFilter({
  title,
  icon: Glyph,
  options,
  selected,
  onToggle,
  onSelectAll,
  onClearAll,
  onOpenChange,
}: {
  title: string;
  icon: LucideIcon;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  /** Told when the list opens or closes: the pill stays open while a list is. */
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <AnchoredPopover onOpenChange={onOpenChange}>
      <AnchoredPopoverTrigger
        render={<button type="button" aria-haspopup="listbox" data-active={selected.length > 0 ? '' : undefined} className="cal-filter-list-button" />}
      >
        <Glyph aria-hidden />
        <span>{title}</span>
        {selected.length > 0 ? <span className="cal-filter-list-count">{selected.length}</span> : null}
        <ChevronDown aria-hidden className="cal-filter-chevron" />
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent align="start" side="bottom" aria-label={title} className="cal-filter-list-popover">
        <section aria-label={title} className="cal-filter-section">
          <div className="cal-filter-bulk">
            <button type="button" onClick={onSelectAll}>
              Select all
            </button>
            <button type="button" onClick={onClearAll}>
              Clear all
            </button>
          </div>
          {/* PM `MultiSelectDropdown`: each option a full-width row (`px-3 py-1.5`, grey-100 hover) with its 16 box, the
              chosen box brand with a white check. */}
          <div className="cal-filter-list" role="listbox" aria-label={title} aria-multiselectable>
            {options.length === 0 ? <div className="cal-filter-none">No options available</div> : null}
            {options.map((o) => {
              const checked = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => onToggle(o.value)}
                  className="cal-filter-option"
                >
                  <span className="cal-filter-box" data-checked={checked ? '' : undefined} aria-hidden>
                    {checked ? <Check strokeWidth={3} /> : null}
                  </span>
                  <span className="cal-filter-option-label">{o.label}</span>
                </button>
              );
            })}
          </div>
        </section>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}

export function CalendarFilterPill({
  waitingRoomCount,
  noShowCount,
  cancelledCount,
  statusOptions = [],
  organizationLocations = [],
  selectedStatusCodes,
  onToggleStatusCode,
  onSetStatusCodes,
  onClearStatusCodes,
  selectedInvoiceStatuses,
  onToggleInvoiceStatus,
  onSetInvoiceStatuses,
  onClearInvoiceStatuses,
  selectedLocationIds,
  onToggleLocation,
}: CalendarFilterPillProps) {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const closing = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const open = hover || focused || pinned || listOpen;
  const enter = () => {
    clearTimeout(closing.current);
    setHover(true);
  };
  const leave = () => {
    clearTimeout(closing.current);
    closing.current = setTimeout(() => setHover(false), CLOSE_DELAY_MS);
  };
  const statuses =
    statusOptions.length > 0
      ? statusOptions.map((s) => ({ value: s.code, label: s.display }))
      : APPOINTMENT_STATUS_CODES.map((code) => ({ value: code, label: code }));
  const active = selectedStatusCodes.length + selectedInvoiceStatuses.length + selectedLocationIds.length;
  return (
    <div
      className="cal-filter-pill"
      data-open={open ? '' : undefined}
      data-active={active > 0 ? '' : undefined}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      <div className="cal-filter-bar">
        <button
          type="button"
          aria-label={active > 0 ? `Filters, ${active} on` : 'Filters'}
          aria-expanded={open}
          data-active={active > 0 ? '' : undefined}
          className="cal-filter-button"
          onClick={() => setPinned((p) => !p)}
        >
          <Funnel aria-hidden />
          {active > 0 ? <span className="cal-filter-badge">{active}</span> : null}
        </button>
        <div className="cal-filter-items" inert={!open}>
          <QuickFilter
            icon={Clock}
            label="Waiting"
            count={waitingRoomCount}
            active={selectedStatusCodes.includes('CheckedIn')}
            onClick={() => onToggleStatusCode('CheckedIn')}
          />
          <QuickFilter
            icon={Ban}
            label="No show"
            count={noShowCount}
            active={selectedStatusCodes.includes('NoShow')}
            onClick={() => onToggleStatusCode('NoShow')}
          />
          <QuickFilter
            icon={CircleX}
            label="Cancelled"
            count={cancelledCount}
            active={selectedStatusCodes.includes('Cancelled')}
            onClick={() => onToggleStatusCode('Cancelled')}
          />
          <ListFilter
            title="Statuses"
            icon={Funnel}
            options={statuses}
            selected={selectedStatusCodes}
            onToggle={onToggleStatusCode}
            onSelectAll={() => onSetStatusCodes(statuses.map((s) => s.value))}
            onClearAll={onClearStatusCodes}
            onOpenChange={setListOpen}
          />
          <ListFilter
            title="Invoice statuses"
            icon={Receipt}
            options={INVOICE_STATUS_OPTIONS.map((v) => ({ value: v, label: v }))}
            selected={selectedInvoiceStatuses}
            onToggle={onToggleInvoiceStatus}
            onSelectAll={() => onSetInvoiceStatuses([...INVOICE_STATUS_OPTIONS])}
            onClearAll={onClearInvoiceStatuses}
            onOpenChange={setListOpen}
          />
          {organizationLocations.map((loc) => {
            const Glyph = iconForLocation(loc.name);
            return (
              <button
                key={loc.id}
                type="button"
                aria-label={`Filter by ${loc.name}`}
                aria-pressed={selectedLocationIds.includes(loc.id)}
                onClick={() => onToggleLocation(loc.id)}
                className="cal-filter-chip"
              >
                <Glyph aria-hidden />
                <span>{loc.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
